import axios from 'axios';
import { BASE_URL } from './helpers';
import { ClipMetadata } from './structs';
import { ClipPrompt } from './structs/clip';
import { MediaItem } from './structs/media';
import { MediaPrompt } from './structs/media-spec';
import { ReelReviewRequest, ReelReviewRequestedResponse } from './structs/reelReview';
import {
  normalizePromptReferenceImageUrls,
  readSceneReferenceImageUrls,
  readTopLevelReferenceImageUrls,
} from './referenceImagePayload';
import {
  ClipStyleSchema,
  ClipStyleSummary,
  normalizeClipStyleList,
  normalizeClipStyleSchema,
} from './clipstyleSchema';
import { isRecord, toStringValue } from './typeHelpers';

// ==================== Cost Types ====================

export interface ClipCostSummary {
  total_usd: number;
  /** Credits the user was charged for the clip (markup included), read from
   *  the credits ledger by the backend. Absent when only provider telemetry
   *  was available — the UI then falls back to pricing total_usd. */
  total_credits?: number;
  event_count: number;
  /** The pipeline run tree this clip was assembled from, and what it cost.
   *  Shared by every draft of that run, so it is shown BESIDE the clip's own
   *  spend, never added into it. Absent for clips made before the backend
   *  stamped billing runs (their one figure already includes it). */
  run_id?: string;
  run_credits?: number;
  run_usd?: number;
  currency?: string;
  providers?: Record<string, { total_usd: number; event_count: number }>;
}

// ==================== Request Types ====================

interface NewClipPromptRequest {
  name?: string;
  imagePrompts?: MediaPrompt[];
  aiVideoPrompts?: MediaPrompt[];
  audioPrompts?: MediaPrompt[];
  music_media_id?: string | null;
  metadata?: ClipMetadata;
  clipStyle?: string;
}

interface EditClipPromptRequest {
  name?: string;
  metadata?: ClipMetadata;
  clipStyle?: string;
  music_media_id?: string | null;
}

interface NewClipIdeaRequest {
  clip_idea: string;
  clip_prompt_json?: string;
  clip_prompt_list?: string[];
}

export interface AvailableMediaItem {
  id: string;
  media_id?: string;
  type: string;
  name: string;
  url?: string;
  mime_type?: string;
  source?: string;
  size_bytes?: number;
  clip_id?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
}

export interface ClipSummary {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  thumbnail_url: string | null;
  is_dirty: boolean;
  media_count: { images: number; ai_videos: number; audios: number };
}

/** Archive render-state filter. The backend applies it store-side. */
export type ClipArchiveFilter = 'all' | 'rendered' | 'unfinished';

export interface PagedClipSummaries {
  items: ClipSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface SwapClipMediaRequest {
  kind: 'image' | 'ai_video' | 'audio';
  media_index: number;
  new_media_id: string;
  /**
   * The media id currently bound to the slot being replaced. When present the
   * store resolves the slot by THIS id (and errors when it is not in the
   * bucket) instead of trusting `media_index` — which callers compute from a
   * delivered array whose order the store never guaranteed, so a positional
   * swap can rebind the wrong slot. Optional only for legacy callers; every
   * call site here sends it.
   */
  old_media_id?: string;
}

// ==================== Helpers ====================

const normalizeAvailableMediaItem = (value: unknown, index: number): AvailableMediaItem | null => {
  if (!isRecord(value)) {
    if (typeof value === 'string') {
      return {
        id: value,
        media_id: value,
        type: 'unknown',
        name: value,
        url: value,
      };
    }
    return null;
  }

  const url = toStringValue(value.url ?? value.file_url ?? value.asset_url ?? value.uri);
  const id = toStringValue(value.id ?? value.media_id ?? value.asset_id, url || `media-${index + 1}`);
  if (!id) return null;
  const metadata = isRecord(value.metadata) ? value.metadata : undefined;
  const sizeBytesRaw = value.size_bytes ?? value.sizeBytes ?? value.size;
  const sizeBytes =
    typeof sizeBytesRaw === 'number'
      ? sizeBytesRaw
      : typeof sizeBytesRaw === 'string' && sizeBytesRaw.trim()
        ? Number(sizeBytesRaw)
        : undefined;

  const normalized: AvailableMediaItem = {
    id,
    media_id: toStringValue(value.media_id ?? value.id ?? value.asset_id, id),
    type: toStringValue(value.type ?? value.media_type ?? value.kind, 'unknown'),
    name: toStringValue(
      value.name ?? value.file_name ?? value.filename ?? value.title,
      id
    ),
  };

  if (url) normalized.url = url;
  const mimeType = toStringValue(value.mime_type ?? value.mimeType ?? value.content_type);
  if (mimeType) normalized.mime_type = mimeType;
  const source = toStringValue(value.source ?? value.origin);
  if (source) normalized.source = source;
  if (Number.isFinite(sizeBytes)) normalized.size_bytes = sizeBytes;
  const clipId = toStringValue(value.clip_id ?? value.clipId);
  if (clipId) normalized.clip_id = clipId;
  const createdAt = toStringValue(value.created_at ?? value.createdAt);
  if (createdAt) normalized.created_at = createdAt;
  if (metadata) normalized.metadata = metadata;

  return normalized;
};

const normalizeAvailableMediaList = (payload: unknown): AvailableMediaItem[] => {
  const items = isRecord(payload)
    ? (Array.isArray(payload.media_files)
      ? payload.media_files
      : Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload.data)
          ? payload.data
          : [])
    : Array.isArray(payload)
      ? payload
      : [];

  return items
    .map((item, index) => normalizeAvailableMediaItem(item, index))
    .filter((item): item is AvailableMediaItem => Boolean(item));
};

const normalizeCreateClipPromptRequest = (
  request: NewClipPromptRequest
): NewClipPromptRequest => {
  const topLevelReferenceImageUrls = readTopLevelReferenceImageUrls(request.metadata);
  // Scene-tagged bindings first: the flat list above is in checkpoint order, so
  // indexing it hands a scene the asset in front of its own whenever the run
  // starts with an upload checkpoint.
  const sceneReferenceImageUrls = readSceneReferenceImageUrls(request.metadata);
  return {
    ...request,
    imagePrompts: normalizePromptReferenceImageUrls(
      request.imagePrompts,
      topLevelReferenceImageUrls,
      sceneReferenceImageUrls
    ),
    aiVideoPrompts: normalizePromptReferenceImageUrls(
      request.aiVideoPrompts,
      topLevelReferenceImageUrls,
      sceneReferenceImageUrls
    ),
  };
};

// ==================== Clip API ====================

const getClipPrompt = (clipId: string) =>
  axios.get(`${BASE_URL}/clips/${encodeURIComponent(clipId)}`).then((res) => res.data.clip_prompt);

/**
 * Lists clip prompts.
 *
 * The default scope is the working set: clips created — or opened out of the
 * archive — within the last 24 hours. Everything older lives in the Clip
 * Archive (`/archive`, {@link listArchivedClips}) and is one click from coming
 * back. Pass `'all'` only where names for long-archived clips are genuinely
 * needed; it makes the backend walk the whole durable list.
 */
const getClipPrompts = (
  scope: 'active' | 'all' = 'active'
): Promise<{ clips: ClipPrompt[]; costByClip: Record<string, ClipCostSummary> }> =>
  axios
    .get(`${BASE_URL}/clips`, scope === 'all' ? { params: { scope: 'all' } } : undefined)
    .then((res) => ({
      clips: res.data.clip_prompts || [],
      costByClip: res.data.cost_summary_by_clip || {},
    }));

const createClipPrompt = (request: NewClipPromptRequest) =>
  axios
    .post(`${BASE_URL}/clips`, normalizeCreateClipPromptRequest(request))
    .then((res) => res.data.clip_prompt_id);

/**
 * Parses a clip prompt JSON and normalizes reference-image metadata.
 */
const createClipPromptFromJson = (json: string) => {
  const parsed = JSON.parse(json) as NewClipPromptRequest;

  return createClipPrompt({
    ...parsed,
  });
};

const editClipPrompt = (clipId: string, request: EditClipPromptRequest) =>
  axios.put(`${BASE_URL}/clips/${clipId}`, request).then((res) => res.data);

const editClipMetadata = async (clipId: string, key: string, value: any) => {
  const clipPrompt = await getClipPrompt(clipId);
  const existingMetadata = clipPrompt?.metadata || {};

  return editClipPrompt(clipId, {
    metadata: {
      ...existingMetadata,
      [key]: value,
    },
  });
};

const getClipStyles = async (): Promise<ClipStyleSummary[]> => {
  const payload = await axios.get(`${BASE_URL}/clipstyles`).then((res) => res.data);
  return normalizeClipStyleList(payload);
};

const getClipStyleSchema = async (
  styleId: string,
  fallbackSummary?: ClipStyleSummary
): Promise<ClipStyleSchema> => {
  const payload = await axios
    .get(`${BASE_URL}/clipstyles/${encodeURIComponent(styleId)}/schema`)
    .then((res) => res.data);

  return normalizeClipStyleSchema(styleId, payload, fallbackSummary);
};

/**
 * Removes a clip's music or narration.
 *
 * "Remove" has to mean *unbind and detach*: the render path (and the editor's
 * mirror of it) auto-wires the first ready audio item on the clip when no
 * `music_media_id` is stored, so clearing the key alone lets the same track
 * resolve again on the next read. The backend deletes the audio item it
 * resolved for the role and clears the key in one 204; the caller refetches.
 */
const deleteClipAudio = (clipId: string, role: 'music' | 'narrator') =>
  axios.delete(`${BASE_URL}/clips/${clipId}/audio/${role}`).then((res) => res.data);

const deleteClipPrompt = (clipId: string) =>
  axios.delete(`${BASE_URL}/clips/${clipId}`).then((res) => res.data);

/** A started render; `estimate_credits` is set for a generated (package) style. */
export interface RenderStarted {
  status: string;
  estimate_credits?: number;
}

/**
 * Starts a render. A built-in style posts no body (unchanged). A generated
 * style may answer 409 `confirm_required` with `estimate_credits`; the caller
 * asks the user and posts again with `confirmCredits` (Phase 4 design § Credits).
 */
const renderClip = (clipId: string, confirmCredits?: number): Promise<RenderStarted> =>
  axios
    .post(`${BASE_URL}/clips/${clipId}/render`, confirmCredits ? { confirm_credits: confirmCredits } : undefined)
    .then((res) => res.data);

/** Cancels a running generated-style render; the seconds that ran are billed. */
const cancelClipRender = (clipId: string) =>
  axios.post(`${BASE_URL}/clips/${clipId}/render/cancel`).then((res) => res.data);

/**
 * Kicks off the whole-reel AI review against the RENDERED reel. 202 means the
 * judge run started; the report itself lands later on `clip.metadata.reel_review`
 * (WS `ReelReviewCompleted` + a clip refetch). 409 means the clip has not been
 * rendered yet — the caller surfaces that as "render the clip first".
 */
const reviewReel = (clipId: string, request: ReelReviewRequest): Promise<ReelReviewRequestedResponse> =>
  axios.post(`${BASE_URL}/clips/${clipId}/review-reel`, request).then((res) => res.data);

// ==================== Clip Ideas API ====================

const getIdeas = () =>
  axios.get(`${BASE_URL}/clip-ideas`).then((res) => res.data.prompt_ideas || []);

const createIdea = (clipIdea: string, clipPromptJson: string) =>
  axios.post(`${BASE_URL}/clip-ideas`, {
    clip_idea: clipIdea,
    clip_prompt_json: clipPromptJson,
  } as NewClipIdeaRequest).then((res) => res.data);

const createIdeas = (clipIdea: string, clipPromptList: string[]) =>
  axios.post(`${BASE_URL}/clip-ideas`, {
    clip_idea: clipIdea,
    clip_prompt_list: clipPromptList,
  } as NewClipIdeaRequest).then((res) => res.data);

const deleteIdea = (clipIdea: string) =>
  axios.delete(`${BASE_URL}/clip-ideas`, {
    data: { clip_idea: clipIdea },
  }).then((res) => res.data);

// ==================== Archived Clips ====================

const listArchivedClips = async (
  page = 1,
  limit = 20,
  search = '',
  filter: ClipArchiveFilter = 'all'
): Promise<PagedClipSummaries> => {
  const params: Record<string, string | number> = { page, limit, filter };
  if (search.trim()) params.search = search.trim();
  const payload = await axios
    .get(`${BASE_URL}/clips/list`, { params })
    .then((res) => res.data);
  const p = isRecord(payload) ? payload : {};
  const rawItems = Array.isArray(p.items) ? p.items : [];
  const items: ClipSummary[] = rawItems.map((item: unknown) => {
    const r = isRecord(item) ? item : {} as Record<string, unknown>;
    const mediaCount = isRecord(r.media_count) ? r.media_count : {};
    return {
      id: toStringValue(r.id, ''),
      name: toStringValue(r.name, ''),
      created_at: toStringValue(r.created_at, ''),
      updated_at: toStringValue(r.updated_at, ''),
      finished_at: toStringValue(r.finished_at) || null,
      thumbnail_url: toStringValue(r.thumbnail_url) || null,
      is_dirty: typeof r.is_dirty === 'boolean' ? r.is_dirty : false,
      media_count: {
        images: typeof mediaCount.images === 'number' ? mediaCount.images : 0,
        ai_videos: typeof mediaCount.ai_videos === 'number' ? mediaCount.ai_videos : 0,
        audios: typeof mediaCount.audios === 'number' ? mediaCount.audios : 0,
      },
    };
  });
  return {
    items,
    total: typeof p.total === 'number' ? p.total : items.length,
    page: typeof p.page === 'number' ? p.page : page,
    limit: typeof p.limit === 'number' ? p.limit : limit,
  };
};

const hydrateClip = (id: string): Promise<any> =>
  axios.post(`${BASE_URL}/clips/${id}/hydrate`).then((res) => res.data);

const unloadClip = (id: string): Promise<void> =>
  axios.post(`${BASE_URL}/clips/${id}/unload`).then(() => undefined);

const swapClipMedia = (clipId: string, req: SwapClipMediaRequest): Promise<any> =>
  axios.post(`${BASE_URL}/clips/${clipId}/media/swap`, req).then((res) => res.data);

/**
 * Copies a clip's image/video item into a second item over the SAME file, with
 * `metadata_overrides` merged over the copy (a `null` value deletes a key).
 * Returns the new MediaItem in full, so the caller can show it without a
 * refetch. Used by the editor's split tool.
 */
const cloneClipMedia = (
  clipId: string,
  mediaId: string,
  metadataOverrides: Record<string, any>
): Promise<MediaItem> =>
  axios
    .post(`${BASE_URL}/clips/${clipId}/media/${mediaId}/clone`, { metadata_overrides: metadataOverrides })
    .then((res) => res.data);

// ==================== Clip-side reviewer (Clip QA) ====================

export interface SelectVariantResponse {
  media_id: string;
  file_url: string;
  selected_variant: string;
}

/**
 * Swaps a reviewed scene item's canonical file to one of its `review_variants`
 * (the original render or one of the reviewer's re-renders). The item's own
 * id never changes — only `file_url`/`selected_variant` — so the editor's
 * preview and a later render just pick up the newly-selected file.
 *
 * `preserveDuration` keeps the item's declared `duration` and trim range across
 * the pick. The takes strip passes it, because a regenerate auto-swaps the item
 * onto its new take WITHOUT clearing them and one row of thumbnails must not
 * have two different effects on the scene's on-screen length. The reviewer
 * panel does not: there the trim keys were computed by a verdict for one
 * specific candidate, and carrying them onto a different one plays it wrong.
 */
const selectVariant = (
  clipId: string,
  mediaId: string,
  variantMediaId: string,
  options: { preserveDuration?: boolean } = {}
): Promise<SelectVariantResponse> =>
  axios
    .post(`${BASE_URL}/clips/${clipId}/media/${mediaId}/select-variant`, {
      variant_media_id: variantMediaId,
      // Sent only when asked for, so the reviewer panel's request stays byte
      // for byte what it always was and the route's default is untouched.
      ...(options.preserveDuration ? { preserve_duration: true } : {}),
    })
    .then((res) => res.data);

// ==================== Scene alternates ====================

/**
 * Where a take came from, and therefore how picking it must bind it. The two
 * are NOT interchangeable — see the strip.
 */
export type SceneAlternateOrigin = 'take' | 'run';

/** One prior generation of a clip scene, as the backend lists it. */
export interface SceneAlternate {
  media_id: string;
  url: string;
  type: string;
  role?: string;
  filename?: string;
  checkpoint_id?: string;
  created_at?: string;
  /** True for the generation the clip is bound to right now. */
  current: boolean;
  /**
   * `take` — a candidate render OF the selected item (its review_variants
   * ledger). Bound with select-variant, which repoints the item's bytes and
   * keeps its id.
   *
   * `run` — a pipeline attachment, a different media item entirely. Bound with
   * swap, which rebinds the clip to that id.
   */
  origin: SceneAlternateOrigin;
  /** The ledger's own label for a `take` row: original / regenerate / rerender. */
  take_source?: string;
}

export interface SceneAlternatesResponse {
  scene_id: string;
  pipeline_run_id: string;
  items: SceneAlternate[];
}

/**
 * The generations of the pipeline scene a clip media item came from, newest
 * first — what the editor's Alternates strip offers instead of a re-render.
 *
 * The backend resolves the scene from provenance the clip already carries
 * (`metadata.pipeline_run_id` on the clip, `scene_id` on the item), so a clip
 * built by hand or assembled before those stamps existed answers with an empty
 * `items` and the strip hides itself.
 *
 * A FAILURE, though, propagates. It used to be swallowed into the same empty
 * list, which made "the backend fell over" indistinguishable from "no history"
 * — a video slide with five takes showed nothing and there was no way to know.
 * `useSceneAlternates` catches the rejection and renders a one-line retry, so
 * the surrounding inspector still can never break on it.
 */
const listSceneAlternates = (clipId: string, mediaId: string): Promise<SceneAlternate[]> =>
  axios
    .get(`${BASE_URL}/clips/${clipId}/media/${mediaId}/alternates`)
    .then((res) => {
      const payload = isRecord(res.data) ? res.data : {};
      return Array.isArray(payload.items)
        ? payload.items
            .filter(isRecord)
            .map((item) => ({
              media_id: toStringValue(item.media_id, ''),
              url: toStringValue(item.url, ''),
              type: toStringValue(item.type, ''),
              role: toStringValue(item.role) || undefined,
              filename: toStringValue(item.filename) || undefined,
              checkpoint_id: toStringValue(item.checkpoint_id) || undefined,
              created_at: toStringValue(item.created_at) || undefined,
              current: item.current === true,
              // A backend that predates the two sources answered with run
              // attachments only, so that is what an unlabelled row is.
              origin: (toStringValue(item.origin) === 'take' ? 'take' : 'run') as SceneAlternateOrigin,
              take_source: toStringValue(item.take_source) || undefined,
            }))
            .filter((item) => Boolean(item.media_id) && Boolean(item.url))
        : [];
    });

// ==================== Available Media ====================

const getAvailableMedia = () =>
  axios
    .get(`${BASE_URL}/clips/available-media`)
    .then((res) => normalizeAvailableMediaList(res.data));

// ==================== Export ====================

const ClipAPI = {
  getClipPrompt,
  getClipPrompts,
  createIdea,
  createIdeas,
  createClipPrompt,
  createClipPromptFromJson,
  editClipPrompt,
  editClipMetadata,
  getClipStyles,
  getClipStyleSchema,
  deleteClipPrompt,
  deleteClipAudio,
  renderClip,
  cancelClipRender,
  reviewReel,
  getAvailableMedia,
  getIdeas,
  deleteIdea,
  listArchivedClips,
  hydrateClip,
  unloadClip,
  swapClipMedia,
  cloneClipMedia,
  selectVariant,
  listSceneAlternates,
};

export default ClipAPI;
